import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import { datosGeneralesService } from '../services/api';

const DatosGenerales = () => {
  const [form, setForm] = useState({
    ruc_empresa: '', razon_social: '', direccion: '', telefono: '',
    usuario_sol: '', clave_sol: '', id_sire: '', clave_sire: '',
    logo_base64: '',
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [alert, setAlert]       = useState(null);
  const [showClaves, setShowClaves] = useState(false);
  const logoInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await datosGeneralesService.get();
      if (res.data && res.data.id_datos) {
        setForm({
          ruc_empresa:  res.data.ruc_empresa  || '',
          razon_social: res.data.razon_social || '',
          direccion:    res.data.direccion    || '',
          telefono:     res.data.telefono     || '',
          usuario_sol:  res.data.usuario_sol  || '',
          clave_sol:    res.data.clave_sol    || '',
          id_sire:      res.data.id_sire      || '',
          clave_sire:   res.data.clave_sire   || '',
          logo_base64:  res.data.logo_base64  || '',
        });
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setAlert({ type: 'error', message: 'Solo el administrador puede acceder a esta seccion.' });
      } else {
        setAlert({ type: 'error', message: 'Error al cargar datos generales.' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 600 * 1024) {
      setAlert({ type: 'warning', message: 'El logo no debe superar 600 KB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, logo_base64: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {};
      Object.entries(form).forEach(([k, v]) => { data[k] = v || null; });
      await datosGeneralesService.save(data);
      setAlert({ type: 'success', message: 'Datos generales guardados correctamente.' });
    } catch (err) {
      if (err.response?.status === 403) {
        setAlert({ type: 'error', message: 'Solo el administrador puede modificar esta seccion.' });
      } else {
        setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Datos Generales" subtitle="Utilitarios">
        <div className="empty-state">Cargando...</div>
      </Layout>
    );
  }

  const sectionTitle = (text) => (
    <div style={{ fontSize: 13, fontWeight: 600, color: '#60a5fa', padding: '18px 0 10px', marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)', letterSpacing: 0.3 }}>
      {text}
    </div>
  );

  return (
    <Layout title="Datos Generales" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Datos Generales de la Empresa</h2>
      </div>

      <div style={{ maxWidth: 700 }}>
        <div className="data-table-container" style={{ padding: 24 }}>
          <form onSubmit={handleSubmit}>

            {/* ── Datos de la Empresa ─────────────────────────────── */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#60a5fa', marginBottom: 16, letterSpacing: 0.3 }}>
              Datos de la Empresa
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0 16px' }}>
              <div className="form-row">
                <label>RUC</label>
                <input name="ruc_empresa" value={form.ruc_empresa} onChange={handleChange} placeholder="20XXXXXXXXX" maxLength={11} />
              </div>
              <div className="form-row">
                <label>Razon Social</label>
                <input name="razon_social" value={form.razon_social} onChange={handleChange} placeholder="Razon social de la empresa" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '0 16px' }}>
              <div className="form-row">
                <label>Direccion</label>
                <input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Direccion fiscal" />
              </div>
              <div className="form-row">
                <label>Telefono</label>
                <input name="telefono" value={form.telefono} onChange={handleChange} placeholder="Telefono" />
              </div>
            </div>

            {/* ── Logo ────────────────────────────────────────────── */}
            {sectionTitle('Logo de la Empresa')}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, paddingBottom: 4 }}>
              {form.logo_base64 ? (
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={form.logo_base64}
                    alt="Logo"
                    style={{ maxHeight: 80, maxWidth: 180, objectFit: 'contain', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', background: '#fff', padding: 8, display: 'block' }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, logo_base64: '' }))}
                    title="Eliminar logo"
                    style={{ position: 'absolute', top: -8, right: -8, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14, lineHeight: '22px', textAlign: 'center', padding: 0 }}
                  >×</button>
                </div>
              ) : (
                <div style={{ width: 96, height: 80, border: '2px dashed rgba(255,255,255,0.15)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="rgba(255,255,255,0.2)">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </div>
              )}
              <div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />
                <button type="button" className="btn btn-secondary" onClick={() => logoInputRef.current?.click()}>
                  {form.logo_base64 ? 'Cambiar Logo' : 'Subir Logo'}
                </button>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, lineHeight: 1.7 }}>
                  Formatos: PNG, JPG, SVG, WebP &nbsp;·&nbsp; Máx. 600 KB<br/>
                  Se mostrará en los documentos impresos.
                </div>
              </div>
            </div>

            {/* ── Credenciales SUNAT SOL ──────────────────────────── */}
            {sectionTitle('Credenciales SUNAT - SOL')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div className="form-row">
                <label>Usuario SOL</label>
                <input name="usuario_sol" value={form.usuario_sol} onChange={handleChange} placeholder="Usuario SOL" />
              </div>
              <div className="form-row">
                <label>Clave SOL</label>
                <div style={{ position: 'relative' }}>
                  <input name="clave_sol" type={showClaves ? 'text' : 'password'} value={form.clave_sol} onChange={handleChange} placeholder="Clave SOL" style={{ paddingRight: 36 }} />
                  <button type="button" onClick={() => setShowClaves(!showClaves)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
                    {showClaves ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Credenciales SIRE ───────────────────────────────── */}
            {sectionTitle('Credenciales SIRE')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div className="form-row">
                <label>ID SIRE</label>
                <input name="id_sire" value={form.id_sire} onChange={handleChange} placeholder="ID Cliente SIRE" />
              </div>
              <div className="form-row">
                <label>Clave SIRE</label>
                <div style={{ position: 'relative' }}>
                  <input name="clave_sire" type={showClaves ? 'text' : 'password'} value={form.clave_sire} onChange={handleChange} placeholder="Clave SIRE" style={{ paddingRight: 36 }} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 16 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
    </Layout>
  );
};

export default DatosGenerales;
